import { useLocation, Location, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState, useContext, useRef, type ReactNode, type RefObject } from 'react';
import { Link as FplLink } from '@figma/fpl-components';
import { ColorTheme } from '@figma/fpl-themes';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as fplColor, font } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { Trans } from '@/UI/Trans/Trans';
import { WeavyTextLogo } from '@/UI/Icons/WeavyTextLogo';
import { Copyrights } from '@/components/Common/Copyrights';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import GoogleLogo from '@/UI/Icons/GoogleLogo';
import MicrosoftLogo from '@/UI/Icons/MicrosoftLogo';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { AuthContext } from '@/contexts/AuthContext';
import { FlexColCenHorVer, Flex, FlexCol, FlexCenVer } from '@/UI/styles';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { useBanner, APP_BANNER_ID } from '@/hooks/useBanner';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import {
  AuthenticationTurnstileWidget,
  AuthenticationTurnstileStatus,
  type AuthenticationTurnstileWidgetHandle,
  isTurnstileBlockingSignIn,
} from '@/components/Authentication/AuthenticationTurnstileWidget';
import { IDP_INITIATED_REDIRECT_PATH, SIGN_IN_REDIRECT_QUERY_PARAM } from '@/consts/auth.consts';
import { clearMobileHandoffSessionMarker, storeMobileHandoffSessionMarker } from '@/consts/mobileHandoff.consts';
import { TurnstileWidgetSize, TurnstileWidgetTheme } from '@/components/Authentication/TurnstileWidget';
import { getTurnstileToken } from '@/services/auth/turnstile-token.service';
import { peekStandaloneToolReturn, tryReturnToToolPage } from '@/services/auth/standalone-tool-return';
import { getSafeRedirectPath } from '@/utils/urls';
import { clearOAuthCallbackParams, getOAuthCallbackParams } from '@/utils/oauthCallbackParams';
import type { SignInOptions } from '@/contexts/AuthContext';

const SIGN_IN_PATH = '/signin';

const useWrappedLocation = () => {
  const location = useLocation();
  return location as Location<{ from?: string }>;
};

// The card follows the Weave website design, not the product UI. It uses the website font
// (Figma Sans, loaded in fonts.css) and sizes larger than the FPL type scale, so the text styles
// below are literal values instead of typography presets.
const FIGMA_SANS = `"Figma Sans", ${font.fontFamilyDefault}`;

const styles = stylex.create({
  title: {
    color: '#282828',
    fontFamily: FIGMA_SANS,
    fontSize: 30,
    fontWeight: 500,
    lineHeight: 1.3,
    marginBottom: 36,
    marginTop: 0,
    textAlign: 'center',
  },
  terms: {
    color: fplColor.textSecondary,
    fontFamily: FIGMA_SANS,
    fontSize: 12,
    letterSpacing: '0.24px',
    lineHeight: '20px',
    marginBottom: 0,
    marginTop: 24,
    textAlign: 'center',
  },
  providerButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 24,
    width: '100%',
  },
  providerButton: {
    alignItems: 'center',
    backgroundColor: {
      default: fplColor.bg,
      ':hover': fplColor.bgHover,
      ':disabled': fplColor.bg,
    },
    borderColor: {
      default: fplColor.icon,
      ':disabled': fplColor.borderDisabled,
    },
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    color: {
      default: fplColor.text,
      ':disabled': fplColor.textDisabled,
    },
    cursor: {
      default: 'pointer',
      ':disabled': 'default',
    },
    display: 'flex',
    fontFamily: FIGMA_SANS,
    fontSize: 18,
    fontWeight: 700,
    gap: 10,
    height: 48,
    justifyContent: 'flex-start',
    outline: {
      default: 'none',
      ':focus-visible': `2px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: 2,
    paddingInlineEnd: 16,
    // A fixed start inset lines the icons up in one column across the buttons.
    paddingInlineStart: {
      default: 24,
      '@media (min-width: 600px)': 70,
    },
    width: '100%',
  },
  providerButtonIcon: {
    display: 'flex',
    flexShrink: 0,
  },
});

/** `Trans` fills in the link text, so the children are optional here. */
function NewTabLink({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <FplLink href={href} newTab>
      {children}
    </FplLink>
  );
}

function SignInProviderButton({
  children,
  disabled,
  icon,
  onClick,
}: {
  children: ReactNode;
  disabled: boolean;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} {...stylex.props(styles.providerButton)}>
      <span aria-hidden {...stylex.props(styles.providerButtonIcon)}>
        {icon}
      </span>
      {children}
    </button>
  );
}

const FirebaseSignInButtons = ({
  signIn,
  from,
  promotion,
  initiateRedirectSignIn,
  turnstileRef,
  isChallengeBlocking,
}: {
  signIn: (options: SignInOptions) => Promise<void>;
  from: string;
  promotion: string | null;
  initiateRedirectSignIn: (provider: string, redirectPath: string) => Promise<void>;
  turnstileRef: RefObject<AuthenticationTurnstileWidgetHandle | null>;
  isChallengeBlocking: boolean;
}) => {
  const { t } = useTranslation();
  const onSignInFlowFailed = useCallback(() => {
    clearMobileHandoffSessionMarker();
    turnstileRef.current?.reset();
  }, [turnstileRef]);

  const googleSignIn = useCallback(() => {
    storeMobileHandoffSessionMarker(from);
    void signIn({
      route: from,
      providerType: 'google',
      promotion,
      turnstileToken: turnstileRef.current?.getToken(),
      onSignInFlowFailed,
    });
  }, [signIn, from, promotion, turnstileRef, onSignInFlowFailed]);

  const microsoftSignIn = useCallback(() => {
    storeMobileHandoffSessionMarker(from);
    void signIn({
      route: from,
      providerType: 'microsoft',
      promotion,
      turnstileToken: turnstileRef.current?.getToken(),
      onSignInFlowFailed,
    });
  }, [signIn, from, promotion, turnstileRef, onSignInFlowFailed]);

  const figmaSignIn = useCallback(() => {
    storeMobileHandoffSessionMarker(from);
    sessionStorage.setItem(IDP_INITIATED_REDIRECT_PATH, from);
    void initiateRedirectSignIn('figma', '/signin');
  }, [from, initiateRedirectSignIn]);

  return (
    <div {...stylex.props(styles.providerButtons)}>
      <SignInProviderButton icon={<FigmaLogo />} onClick={figmaSignIn} disabled={isChallengeBlocking}>
        {t(I18N_KEYS.SIGN_IN_PAGE.FIGMA_SIGN_IN_BUTTON)}
      </SignInProviderButton>
      <SignInProviderButton
        icon={<GoogleLogo width={20} height={20} />}
        onClick={googleSignIn}
        disabled={isChallengeBlocking}
      >
        {t(I18N_KEYS.SIGN_IN_PAGE.GOOGLE_SIGN_IN_BUTTON)}
      </SignInProviderButton>
      <SignInProviderButton
        icon={<MicrosoftLogo width={20} height={20} />}
        onClick={microsoftSignIn}
        disabled={isChallengeBlocking}
      >
        {t(I18N_KEYS.SIGN_IN_PAGE.MICROSOFT_SIGN_IN_BUTTON)}
      </SignInProviderButton>
    </div>
  );
};

export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useWrappedLocation();
  const [searchParams] = useSearchParams();
  /** Keeps the OAuth callback identifiable after sign-in clears the shared window handoff. */
  const [oauthCallbackParams] = useState(getOAuthCallbackParams);
  const promotion = searchParams.get('promotion');
  const { offset } = useBanner(APP_BANNER_ID);
  // A tool page is not this app, so it leaves its return address in the tab instead of router state.
  // Read once, or the answer changes the moment the note is used up and a destination shifts mid-use.
  const [standaloneToolReturn] = useState(peekStandaloneToolReturn);
  // Snapshotted for the same reason as the OAuth handoff: signing in unmounts this subtree
  // (AuthProvider renders a loader while `isSigningInRef` is set), and the remounted copy no longer
  // holds the callback code that tells the effects below an OAuth return is in flight. Without this
  // the remount falls back to '/' and sends a Figma community visitor to the dashboard instead of
  // the workflow they followed.
  const [idpInitiatedRedirectPath] = useState(() => sessionStorage.getItem(IDP_INITIATED_REDIRECT_PATH));
  const redirectParam = searchParams.get(SIGN_IN_REDIRECT_QUERY_PARAM);
  const redirectPath = redirectParam ? getSafeRedirectPath(redirectParam) : undefined;
  const from =
    typeof location.state?.from === 'string'
      ? getSafeRedirectPath(location.state.from)
      : (standaloneToolReturn ?? redirectPath ?? getSafeRedirectPath(idpInitiatedRedirectPath));
  const { currentUser, signIn, isSigningIn, isSignInRedirectPending, initiateRedirectSignIn, getStoredPromotion } =
    useContext(AuthContext);
  const [authChecked, setAuthChecked] = useState(false);
  const [isSigninInProgress, setIsSigninInProgress] = useState(false);
  const [turnstileStatus, setTurnstileStatus] = useState<AuthenticationTurnstileStatus>(
    AuthenticationTurnstileStatus.Pending,
  );
  const [pendingFigmaSignIn, setPendingFigmaSignIn] = useState<SignInOptions | null>(null);
  const isSignInBlockedByTurnstile = isTurnstileBlockingSignIn(turnstileStatus);
  const turnstileRef = useRef<AuthenticationTurnstileWidgetHandle>(null);
  const submittedFigmaCodeRef = useRef<string | null>(null);

  const submitFigmaSignIn = useCallback(
    (options: SignInOptions, turnstileToken: string | undefined) => {
      if (!options.authorizationCode || submittedFigmaCodeRef.current === options.authorizationCode) {
        return;
      }
      submittedFigmaCodeRef.current = options.authorizationCode;
      setPendingFigmaSignIn(null);
      setIsSigninInProgress(true);
      const signInPromise = signIn({
        ...options,
        turnstileToken,
        onSignInFlowFailed: () => {
          clearMobileHandoffSessionMarker();
          turnstileRef.current?.reset();
        },
      });
      // The provider synchronously captures state before its first network await.
      clearOAuthCallbackParams();
      void signInPromise.catch(() => setIsSigninInProgress(false));
    },
    [signIn],
  );

  // Handle Figma OAuth redirect
  useEffect(() => {
    const authorizationCode = oauthCallbackParams.code;
    if (
      authorizationCode &&
      !currentUser &&
      !isSigningIn &&
      !pendingFigmaSignIn &&
      submittedFigmaCodeRef.current !== authorizationCode
    ) {
      // For Figma OAuth, use stored promotion and redirect path (post-login redirect) from sessionStorage
      const storedPromotion = getStoredPromotion();
      const storedRedirectPath = sessionStorage.getItem(IDP_INITIATED_REDIRECT_PATH);

      // `from` already falls back to a standalone tool page's note, so this covers Figma sign-in too.
      const route = getSafeRedirectPath(storedRedirectPath ?? from);
      const options: SignInOptions = {
        route,
        providerType: 'figma',
        authorizationCode,
        promotion: storedPromotion ?? promotion,
      };
      const turnstileToken = getTurnstileToken();
      if (turnstileToken) {
        submitFigmaSignIn(options, turnstileToken);
      } else {
        setPendingFigmaSignIn(options);
        setIsSigninInProgress(false);
      }
    } else if (!authorizationCode) {
      setIsSigninInProgress(false);
    }
  }, [
    oauthCallbackParams.code,
    currentUser,
    isSigningIn,
    pendingFigmaSignIn,
    from,
    promotion,
    getStoredPromotion,
    submitFigmaSignIn,
  ]);

  useEffect(() => {
    if (!pendingFigmaSignIn || currentUser || isSigningIn || isSignInBlockedByTurnstile) {
      return;
    }

    const turnstileToken =
      turnstileStatus === AuthenticationTurnstileStatus.Ready ? turnstileRef.current?.getToken() : undefined;
    if (turnstileStatus === AuthenticationTurnstileStatus.Ready && !turnstileToken) {
      turnstileRef.current?.reset();
      return;
    }
    submitFigmaSignIn(pendingFigmaSignIn, turnstileToken);
  }, [pendingFigmaSignIn, currentUser, isSigningIn, isSignInBlockedByTurnstile, turnstileStatus, submitFigmaSignIn]);

  useEffect(() => {
    if (currentUser && !isSigningIn) {
      // Get the redirect path from location state, default to '/' if not present
      const authorizationCode = oauthCallbackParams.code;
      if (authorizationCode || isSignInRedirectPending) {
        // Navigation is handled directly in the oauth flow. `isSignInRedirectPending` covers the
        // copy of this page that mounts behind the loader: it has no callback code left to recognise
        // the return by, and the location is still /signin until the redirect arrives, so both
        // guards below would otherwise let it navigate over the real destination.
        return;
      }
      // AuthContext already sends somebody onward when a sign-in completes, and this screen can run
      // once more behind it. Navigating again would drop the `?welcome=` it added. This branch is
      // for the visitor who was signed in before they got here, and they are still on /signin.
      if (location.pathname !== SIGN_IN_PATH) {
        return;
      }
      if (!tryReturnToToolPage(from)) {
        navigate(from);
      }
    } else if (!currentUser) {
      setAuthChecked(true);
    }
  }, [currentUser, isSigningIn, isSignInRedirectPending, navigate, from, oauthCallbackParams.code, location.pathname]);

  const goHome = useCallback(() => (window.location.href = 'https://weave.figma.com'), []);

  if (!authChecked) {
    return null;
  }
  if (isSigninInProgress) {
    return <PageLoader />;
  }

  // TODO(figma-embed): defense-in-depth — detect Figma embed mode (e.g. via
  // `isFigmaEmbedRoute() || isInsideIframe()` from `@/utils/auth.utils`) and render an
  // inline access error instead of the signin UI. OAuth popups don't work inside the
  // Figma iframe and the CSP frame-ancestors blocks full-page navigation, so any caller
  // that misses the embed check and lands here should fail visibly rather than show a
  // broken sign-in screen.

  return (
    <ColorTheme mode="light" brand="weave">
      <Flex
        sx={{
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: `calc(100vh - ${offset}px)`,
          width: '100%',
          bgcolor: color.White100,
          position: 'relative',
          backgroundImage:
            'linear-gradient(0deg, #ffffff4d 34%, #c1cdd559 71%), url(https://cdn.prod.website-files.com/681b040781d5b5e278a69989/681ccdbeb607e939f7db68fa_BG%20NET%20Hero.avif)',
          backgroundPosition: '0 0, 50%',
          backgroundSize: 'auto, cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <FlexCol sx={{ flex: 1 }}>
          <Flex sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <WeavyTextLogo style={{ color: color.Black100, cursor: 'pointer' }} onClick={goHome} />
          </Flex>
          <Flex sx={{ display: { xs: 'flex', sm: 'none' }, position: 'absolute', top: 12, left: 12 }}>
            <WeavyLogoFilled
              title="Figma Weave Logo"
              width={36}
              height={36}
              style={{
                color: color.Black100,
                cursor: 'pointer',
              }}
              onClick={goHome}
            />
          </Flex>

          {/* Main Content */}
          <Flex
            data-testid="signin-page-content"
            sx={{
              flexDirection: 'column',
              width: { xs: '100%', sm: '444px' },
              height: { xs: '100%', sm: 'auto' },
              margin: { xs: '0', sm: 'auto' },
              px: { xs: 3, sm: 6 },
              py: { xs: 6, sm: '84px' },
              boxSizing: 'border-box',
              borderRadius: { xs: 0, sm: cssVars.radius.large },
              bgcolor: cssVars.color.bg,
              boxShadow: { xs: 'none', sm: cssVars.elevation.x100 },
              // The flexible Turnstile widget is at least 300px wide. Below this width the usual
              // padding leaves less room than that, and the widget runs off the screen.
              '@media (max-width: 347.95px)': { px: '10px' },
            }}
          >
            <FlexColCenHorVer sx={{ width: '100%', maxWidth: '344px', mx: 'auto' }}>
              <h1 {...stylex.props(styles.title)}>{t(I18N_KEYS.SIGN_IN_PAGE.TITLE)}</h1>
              <AuthenticationTurnstileWidget
                ref={turnstileRef}
                onStatusChange={setTurnstileStatus}
                theme={TurnstileWidgetTheme.Light}
                size={TurnstileWidgetSize.Flexible}
              />
              <FirebaseSignInButtons
                turnstileRef={turnstileRef}
                isChallengeBlocking={isSignInBlockedByTurnstile}
                signIn={signIn}
                from={from}
                promotion={promotion}
                initiateRedirectSignIn={initiateRedirectSignIn}
              />
              <p {...stylex.props(styles.terms)}>
                <Trans
                  i18nKey={I18N_KEYS.SIGN_IN_PAGE.TERMS_MESSAGE}
                  components={{
                    terms: <NewTabLink href={EXTERNAL_LINKS.termsOfService} />,
                    privacy: <NewTabLink href={EXTERNAL_LINKS.privacyPolicy} />,
                  }}
                />
              </p>
            </FlexColCenHorVer>
          </Flex>
        </FlexCol>
        <FlexCenVer
          sx={{
            py: 3,
            px: 3,
            justifyContent: { xs: 'center', sm: 'flex-end' },
          }}
        >
          <Copyrights textColor={color.Black84_T} />
        </FlexCenVer>
      </Flex>
    </ColorTheme>
  );
}
